import { FC, useEffect, useRef, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';

import { GiftDeliveryFeePreviewState } from '../hooks/useGiftDeliveryFeePreview';
import { isFreeBaseFeeApplied } from '../utils/giftDeliveryFeePreview.util';
import {
  FreeShippingSuggestion,
  SurchargeSuggestion,
} from '../utils/giftFreeShippingNudge.util';
import { GiftSurchargeNudgeLine } from './GiftSurchargeNudgeLine';

interface IProps {
  label: string;
  disabled: boolean;
  onPay: () => void;
  deliveryFee: GiftDeliveryFeePreviewState;
  freeShippingSuggestion?: FreeShippingSuggestion | null;
  onAddForFreeShipping?: (suggestion: FreeShippingSuggestion) => void;
  /** 소량주문 추가금이 붙은 라인 — "N개 더 담으면 추가금이 빠져요" */
  surchargeSuggestion?: SurchargeSuggestion | null;
  onAddForSurcharge?: (suggestion: SurchargeSuggestion) => void;
  /** 여러 상품 결제(장바구니)면 추가금 안내에 상품명을 붙인다 */
  showItemName?: boolean;
}

/** 페이지 루트의 기본 하단 여백(pb-20) — 바가 이보다 높아진 만큼만 스페이서로 보탠다 */
const BASE_BOTTOM_PADDING_PX = 80;

/**
 * 체크아웃 하단 고정 결제 바 (장바구니 결제·바로구매 공용).
 * 결제 버튼 바로 위에 "N개 더 담으면 기본배송비 무료" 한 줄 — 배송지 아래 상세 안내(GiftFreeShippingNudge)의 요약판.
 * 결제 직전에 마지막으로 눈에 들어오는 자리라, 스크롤을 올리지 않고도 바로 담을 수 있게 한다.
 */
export const GiftCheckoutPayBar: FC<IProps> = ({
  label,
  disabled,
  onPay,
  deliveryFee,
  freeShippingSuggestion,
  onAddForFreeShipping,
  surchargeSuggestion,
  onAddForSurcharge,
  showItemName = false,
}) => {
  const { preview, loading } = deliveryFee;
  const deliverable = preview?.applicable && preview.deliverable ? preview : null;
  const remaining =
    deliverable &&
    deliverable.rule &&
    deliverable.baseFee > 0 &&
    !isFreeBaseFeeApplied(deliverable)
      ? deliverable.rule.freeOverAmount - deliverable.subtotal
      : 0;
  const freeApplied = !!deliverable && isFreeBaseFeeApplied(deliverable);

  // 안내 줄 유무·글자 확대로 바 높이가 변한다 → 실측해 본문이 가려지지 않게 스페이서로 보탠다
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setBarHeight(el.offsetHeight));
    observer.observe(el);
    setBarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div style={{ height: Math.max(barHeight - BASE_BOTTOM_PADDING_PX + 8, 0) }} />
      <div
        ref={barRef}
        className="fixed bottom-0 left-0 right-0 border-t bg-white p-3 shadow-lg"
      >
        <div className="mx-auto max-w-lg">
          {surchargeSuggestion && (
            <div className="mb-2">
              <GiftSurchargeNudgeLine
                suggestion={surchargeSuggestion}
                onAdd={onAddForSurcharge}
                showItemName={showItemName}
              />
            </div>
          )}
          {remaining > 0 && deliverable && (
            <div className="mb-2 flex items-center justify-between gap-2 text-xs">
              <p className="min-w-0 break-keep text-gray-700">
                {freeShippingSuggestion ? (
                  <>
                    <span className="font-bold theme-text-main">
                      {freeShippingSuggestion.addQty}개
                    </span>
                    만 더 담으면 기본배송비 {formatPrice(deliverable.baseFee)}원 무료
                    <span className="text-gray-400">
                      {' '}
                      (+{formatPrice(freeShippingSuggestion.addCost)}원)
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-bold theme-text-main">
                      {formatPrice(remaining)}원
                    </span>
                    만 더 담으면 기본배송비 {formatPrice(deliverable.baseFee)}원 무료
                  </>
                )}
              </p>
              {freeShippingSuggestion && onAddForFreeShipping && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => onAddForFreeShipping(freeShippingSuggestion)}
                  className="flex-shrink-0 whitespace-nowrap rounded-full border px-3 py-1 font-bold theme-boder-main theme-text-main disabled:opacity-50"
                >
                  {freeShippingSuggestion.addQty}개 더 담기
                </button>
              )}
            </div>
          )}
          {freeApplied && (
            <p className="mb-2 text-center text-xs font-bold text-green-700">
              ✓ 기본배송비 무료가 적용됐어요
            </p>
          )}
          <button
            type="button"
            onClick={onPay}
            disabled={disabled}
            className="w-full rounded-lg bg-red-500 py-3 text-lg font-bold text-white disabled:bg-gray-300"
          >
            {label}
          </button>
        </div>
      </div>
    </>
  );
};
